import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { organizationAPI, searchAPI, getImageUrl } from '../services/api';
import OrganizationSelectorDialog from '../components/dialogs/OrganizationSelectorDialog';
import PersonSelectorDialog from '../components/dialogs/PersonSelectorDialog';
import './FacialSearch.css';

interface SearchResult {
  person_id: string;
  key: string;
  organization_id: string;
  organization_name: string;
  similarity_score: number;
  distance: number;
  image_url?: string;
  image_id?: string;
}

interface SearchResponse {
  query_image: string;
  query_image_id?: string;
  faces_detected: number;
  results: SearchResult[];
  search_time_ms: number;
  upload_io_time_ms?: number;
  validation_quality_time_ms?: number;
  embedding_time_ms: number;
  milvus_search_time_ms: number;
  mongodb_search_time_ms: number;
  unaccounted_time_ms?: number;
}

interface OrganizationOption {
  _id: string;
  name: string;
}

interface PersonOption {
  _id: string;
  key: string;
  organization_id: string;
  image_url?: string;
  created_at: string;
}

type TabType = 'upload' | 'webcam';

function FacialSearch() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('upload');
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<SearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [queryImage, setQueryImage] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationOption | null>(null);
  const selectedOrganizationIdRef = useRef<string>('');
  
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [selectedDetector, setSelectedDetector] = useState<string>('mtcnn');
  const [checkValidation, setCheckValidation] = useState<boolean>(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string>('');
  const [selectedPerson, setSelectedPerson] = useState<PersonOption | null>(null);
  const selectedPersonIdRef = useRef<string>('');
  const [showPersonModal, setShowPersonModal] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [webcamActive, setWebcamActive] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const [lastSearchTimestamp, setLastSearchTimestamp] = useState<string>('');
  const [lastQueryImageId, setLastQueryImageId] = useState<string>('');

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const response = await organizationAPI.list();
        setOrganizations(response.data);
      } catch (err: any) {
        console.error('Erro ao carregar organizações:', err);
        setError('Não foi possível carregar as organizações.');
      }
    };

    loadOrganizations();
  }, []);

  useEffect(() => {
    if (organizationId) {
      setSelectedOrganizationId(organizationId);
      const org = organizations.find(o => o._id === organizationId);
      setSelectedOrganization(org || null);
    } else {
      setSelectedOrganizationId('');
      setSelectedOrganization(null);
    }
  }, [organizationId, organizations]);

  useEffect(() => {
    selectedOrganizationIdRef.current = selectedOrganizationId;
  }, [selectedOrganizationId]);

  useEffect(() => {
    selectedPersonIdRef.current = selectedPersonId;
  }, [selectedPersonId]);

  const handleOrganizationSelect = (org: OrganizationOption) => {
    setSelectedOrganizationId(org._id);
    setSelectedOrganization(org);
    setSelectedPersonId('');
    setSelectedPerson(null);
    navigate(`/search/${org._id}`);
  };

  const handlePersonSelect = (person: PersonOption) => {
    if (person._id) {
      setSelectedPersonId(person._id);
      setSelectedPerson(person);
    } else {
      setSelectedPersonId('');
      setSelectedPerson(null);
    }
    setResult(null);
    setUploadedImage(null);
    setQueryImage(null);
  };

  const markSearchTimestamp = () => {
    const searchStartTime = new Date();
    setLastSearchTimestamp(
      searchStartTime.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    );
  };

  const applySearchResponse = (data: SearchResponse) => {
    setResult(data);
    if (data.query_image_id) {
      setLastQueryImageId(data.query_image_id);
    }
  };

  const buildErrorResult = (detail: any): SearchResponse | null => {
    if (!detail || typeof detail !== 'object') return null;

    const hasTiming =
      detail.search_time_ms !== undefined ||
      detail.embedding_time_ms !== undefined ||
      detail.milvus_search_time_ms !== undefined ||
      detail.mongodb_search_time_ms !== undefined;

    if (!hasTiming) return null;

    return {
      query_image: detail.query_image || 'Erro na busca',
      query_image_id: detail.query_image_id,
      faces_detected: 0,
      results: [],
      search_time_ms: Number(detail.search_time_ms ?? 0),
      upload_io_time_ms: Number(detail.upload_io_time_ms ?? 0),
      validation_quality_time_ms: Number(detail.validation_quality_time_ms ?? 0),
      embedding_time_ms: Number(detail.embedding_time_ms ?? 0),
      milvus_search_time_ms: Number(detail.milvus_search_time_ms ?? 0),
      mongodb_search_time_ms: Number(detail.mongodb_search_time_ms ?? 0),
      unaccounted_time_ms: Number(detail.unaccounted_time_ms ?? 0),
    };
  };

  const handleSearchError = (err: any, fallbackMessage: string) => {
    const detail = err.response?.data?.detail;
    const message = typeof detail === 'string' ? detail : detail?.message;
    setError(message || fallbackMessage);

    const errorResult = buildErrorResult(detail);
    if (errorResult) {
      setResult(errorResult);
      if (errorResult.query_image_id) {
        setLastQueryImageId(errorResult.query_image_id);
      }
    } else {
      setResult(null);
    }
  };

  const requireOrganization = () => {
    if (!selectedOrganizationId) {
      setError('Selecione uma organização para realizar a busca.');
      return false;
    }
    return true;
  };

  // Função para iniciar webcam
  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream as any;
        streamRef.current = stream;
        setWebcamActive(true);
        setError(null);
        // Força play do vídeo
        videoRef.current.play().catch(err => console.error('Erro ao fazer play:', err));
      }
    } catch (err: any) {
      console.error('Webcam error:', err);
      setError(`Não foi possível acessar a webcam: ${err.message}`);
    }
  };

  // Função para parar webcam
  const stopWebcam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setWebcamActive(false);
    }
  };



  // Função para capturar e buscar uma única vez
  const captureAndSearchOnce = async () => {
    if (!videoRef.current || !canvasRef.current || isLoading) return;
    if (!requireOrganization()) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const context = canvasRef.current.getContext('2d');
      if (!context) return;

      // Capturar frame atual
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0);

      // Converter para blob e fazer busca
      canvasRef.current.toBlob(async (blob) => {
        if (!blob) {
          setIsLoading(false);
          return;
        }

        try {
          markSearchTimestamp();
          
          const file = new File([blob], 'webcam-snapshot.jpg', { type: 'image/jpeg' });
          
          // Armazenar thumbnail da captura
          const blobUrl = URL.createObjectURL(blob);
          setQueryImage(blobUrl);
          
          console.log('📸 Captura única - buscando', selectedPersonId ? `(filtrado por pessoa: ${selectedPerson?.key})` : '(todas as pessoas)');
          const response = await searchAPI.search(file, selectedOrganizationId, selectedPersonId || undefined, selectedDetector, checkValidation);
          applySearchResponse(response.data);
        } catch (err: any) {
          handleSearchError(err, 'Erro ao buscar faces');
        } finally {
          setIsLoading(false);
        }
      }, 'image/jpeg', 0.95);
    } catch (err) {
      console.error('Erro na captura única:', err);
      setError('Erro ao capturar imagem');
      setIsLoading(false);
    }
  };

  // Cleanup ao desmontar componente
  useEffect(() => {
    return () => {
      stopWebcam();
    };
  }, []);

  const onDrop = async (acceptedFiles: File[]) => {
    if (!requireOrganization()) return;
    const file = acceptedFiles[0];
    if (file) {
      setIsLoading(true);
      setError(null);
      
      // Mostrar preview da imagem
      const imageUrl = URL.createObjectURL(file);
      setUploadedImage(imageUrl);
      
      try {
        markSearchTimestamp();
        console.log('🔍 Executando busca por upload', selectedPersonId ? `(filtrado por pessoa: ${selectedPerson?.key})` : '(todas as pessoas)');
        const response = await searchAPI.search(file, selectedOrganizationId, selectedPersonId || undefined, selectedDetector, checkValidation);
        applySearchResponse(response.data);
      } catch (err: any) {
        handleSearchError(err, 'Erro ao buscar faces');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resendLastImage = async () => {
    if (!requireOrganization() || !lastQueryImageId) return;

    try {
      setIsLoading(true);
      setError(null);
      markSearchTimestamp();

      console.log('🔁 Reenviando última imagem por query_image_id', lastQueryImageId);
      const response = await searchAPI.searchByImageId(
        lastQueryImageId,
        selectedOrganizationId,
        selectedPersonId || undefined,
        selectedDetector,
        checkValidation
      );
      applySearchResponse(response.data);
    } catch (err: any) {
      handleSearchError(err, 'Erro ao reenviar a última imagem');
    } finally {
      setIsLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    multiple: false,
    disabled: !selectedOrganizationId
  });

  return (
    <div className="facial-search-container">
      {/* Badge de organização no topo */}
      <div className="organization-badge-container">
        {selectedOrganization ? (
          <div 
            className="organization-badge clickable"
            onClick={() => setShowOrgModal(true)}
            title="Clique para trocar de organização"
          >
            <span className="badge-icon">🏢</span>
            <span className="badge-name">{selectedOrganization.name}</span>
            <span className="badge-change">↻</span>
          </div>
        ) : (
          <button 
            className="btn-select-org"
            onClick={() => setShowOrgModal(true)}
          >
            🏢 Selecionar Organização
          </button>
        )}
      </div>

      {/* Título da página */}
      <div className="page-title">
        <h1>🔍 Busca Facial</h1>
        <p className="subtitle">Encontre pessoas similares por imagem ou webcam</p>
      </div>

      {/* Filtros e controles */}
      <div className="filters-container">
        <div className="selectors-row">
          <div className="detector-selector">
            <label htmlFor="detector-select" className="selector-label">Detector:</label>
            <select 
              id="detector-select"
              value={selectedDetector}
              onChange={(e) => setSelectedDetector(e.target.value)}
              className="detector-dropdown"
            >
              <option value="retinaface">RetinaFace</option>
              <option value="mtcnn">MTCNN</option>
              <option value="ssd">SSD</option>
            </select>
          </div>
          <div className="spoof-selector">
            <label htmlFor="validation-check" className="selector-label">Validação:</label>
            <select 
              id="validation-check"
              value={checkValidation ? 'true' : 'false'}
              onChange={(e) => setCheckValidation(e.target.value === 'true')}
              className="spoof-dropdown"
            >
              <option value="false">Não</option>
              <option value="true">Sim</option>
            </select>
          </div>
        </div>
        <div className="filter-badges">
          {selectedOrganizationId && (
            selectedPerson ? (
              <div 
                className="person-badge clickable"
                onClick={() => setShowPersonModal(true)}
                title="Clique para trocar de pessoa"
              >
                <span className="badge-icon">👤</span>
                <span className="badge-name">{selectedPerson.key}</span>
                <span className="badge-change">↻</span>
              </div>
            ) : (
              <button 
                className="btn-select-person"
                onClick={() => setShowPersonModal(true)}
              >
                👤 Pessoa
              </button>
            )
          )}
          {lastQueryImageId && (
            <button
              className="btn-resend-image"
              onClick={resendLastImage}
              disabled={isLoading || !selectedOrganizationId}
              title={`Reenviar query_image_id: ${lastQueryImageId}`}
            >
              🔁 Reenviar
            </button>
          )}
        </div>
      </div>

      {/* Abas de navegação */}
      {selectedOrganizationId && (
      <>
      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('upload');
            stopWebcam();
          }}
        >
          📤 Upload de Imagem
        </button>
        <button
          className={`tab-button ${activeTab === 'webcam' ? 'active' : ''}`}
          onClick={() => setActiveTab('webcam')}
        >
          📷 Câmera
        </button>
      </div>

      {/* Aba: Upload */}
      {activeTab === 'upload' && (
        <div className="tab-content webcam-with-results">
          <div className="webcam-column">
            <UploadTab
              uploadedImage={uploadedImage}
              isLoading={isLoading}
              error={error}
              onDrop={onDrop}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              isDragActive={isDragActive}
            />
          </div>

          {/* Resultados ao lado */}
          {result && (
            <div className="results-column">
              <ResultsSection result={result} getImageUrl={getImageUrl} lastSearchTimestamp={lastSearchTimestamp} queryImage={uploadedImage} error={error} isLoading={isLoading} />
            </div>
          )}
        </div>
      )}

      {/* Aba: Webcam */}
      {activeTab === 'webcam' && (
        <div className="tab-content webcam-with-results">
          <div className="webcam-column">
            <WebcamTab
              videoRef={videoRef}
              canvasRef={canvasRef}
              webcamActive={webcamActive}
              isLoading={isLoading}
              error={error}
              onStartWebcam={startWebcam}
              onStopWebcam={stopWebcam}
              onCaptureOnce={captureAndSearchOnce}
            />
          </div>

          {/* Resultados ao lado */}
          {result && (
            <div className="results-column">
              <ResultsSection result={result} getImageUrl={getImageUrl} lastSearchTimestamp={lastSearchTimestamp} queryImage={queryImage} error={error} isLoading={isLoading} />
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* Modal de Seleção de Organização */}
      <OrganizationSelectorDialog
        show={showOrgModal}
        selectedOrganizationId={selectedOrganizationId}
        onSelect={handleOrganizationSelect}
        onClose={() => setShowOrgModal(false)}
      />

      {/* Modal de Seleção de Pessoa */}
      {selectedOrganizationId && (
        <PersonSelectorDialog
          show={showPersonModal}
          organizationId={selectedOrganizationId}
          selectedPersonId={selectedPersonId}
          onSelect={handlePersonSelect}
          onClose={() => setShowPersonModal(false)}
        />
      )}
    </div>
  );
}

// Componente: Upload Tab
interface UploadTabProps {
  uploadedImage: string | null;
  isLoading: boolean;
  error: string | null;
  onDrop: (files: File[]) => void;
  getRootProps: any;
  getInputProps: any;
  isDragActive: boolean;
}

function UploadTab({
  uploadedImage,
  isLoading,
  error,
  getRootProps,
  getInputProps,
  isDragActive
}: UploadTabProps) {
  return (
    <div className="upload-tab">
      <div className="upload-area" {...getRootProps()}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <div className="dropzone-content active">
            <p>📸 Solte a imagem aqui...</p>
          </div>
        ) : (
          <div className="dropzone-content">
            <p>📸 Arraste uma imagem aqui</p>
            <p className="small">ou clique para selecionar</p>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>🔍 Processando imagem e buscando faces similares...</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
        </div>
      )}

      {uploadedImage && !isLoading && (
        <div className="preview-section">
          <h3>Imagem carregada:</h3>
          <img src={uploadedImage} alt="Preview" className="preview-image" />
        </div>
      )}
    </div>
  );
}

// Componente: Webcam Tab
interface WebcamTabProps {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  webcamActive: boolean;
  isLoading: boolean;
  error: string | null;
  onStartWebcam: () => void;
  onStopWebcam: () => void;
  onCaptureOnce: () => void;
}

function WebcamTab({
  videoRef,
  canvasRef,
  webcamActive,
  isLoading,
  error,
  onStartWebcam,
  onStopWebcam,
  onCaptureOnce
}: WebcamTabProps) {
  return (
    <div className="webcam-tab">
      {/* Container com tamanho fixo - evita piscagem da tela */}
      <div className="webcam-container">
        {/* Sempre manter o elemento video no DOM para evitar ref null */}
        <video
          ref={videoRef}
          className="webcam-video"
          style={{ display: webcamActive ? 'block' : 'none' }}
          autoPlay
          playsInline
          muted
        />

        {/* Placeholder quando webcam está desabilitada */}
        {!webcamActive && (
          <div className="webcam-placeholder">
            <button className="btn-primary btn-large" onClick={onStartWebcam}>
              📷 Iniciar
            </button>
          </div>
        )}
      </div>

      {webcamActive && (
        <div className="webcam-active-section">
          <div className="webcam-controls">
            <button
              className="btn-primary btn-large"
              onClick={onCaptureOnce}
              disabled={isLoading}
              title="Captura uma foto e busca faces similares"
            >
              📸 Capturar
            </button>
            <button
              className="btn-secondary"
              onClick={onStopWebcam}
              disabled={isLoading}
            >
              ⏹️ Parar Webcam
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}

// Componente: Results Section
interface ResultsSectionProps {
  result: SearchResponse;
  getImageUrl: (url?: string) => string;
  lastSearchTimestamp?: string;
  queryImage?: string | null;
  error?: string | null;
  isLoading?: boolean;
}

function ResultsSection({ result, getImageUrl, lastSearchTimestamp, queryImage, error, isLoading }: ResultsSectionProps) {
  const getScoreColor = (score: number): string => {
    if (score > 0.8) return '#4caf50';
    if (score > 0.6) return '#ff9800';
    return '#f44336';
  };

  const uploadIoTimeMs = result.upload_io_time_ms ?? 0;
  const validationQualityTimeMs = result.validation_quality_time_ms ?? 0;
  const unaccountedTimeMs = result.unaccounted_time_ms ?? 0;

  return (
    <div className="results-section">
      <div className="results-header">
        <div className="results-header-top">
          <div>
            <h2>✅ Resultados da Busca</h2>
            <p className="search-info">
              {lastSearchTimestamp && <span>🕐 Última busca: {lastSearchTimestamp} | </span>}
              ⏱️ Tempo Total: {result.search_time_ms.toFixed(2)}ms
            </p>
            <p className="search-breakdown">
              (Upload/IO: {uploadIoTimeMs.toFixed(2)}ms | Validação/Qualidade/Spoof: {validationQualityTimeMs.toFixed(2)}ms | Embedding: {result.embedding_time_ms.toFixed(2)}ms | Milvus: {result.milvus_search_time_ms.toFixed(2)}ms | MongoDB: {result.mongodb_search_time_ms.toFixed(2)}ms | Outros: {unaccountedTimeMs.toFixed(2)}ms)
            </p>
          </div>
          {queryImage && (
            <div className="query-image-thumb">
              <img src={queryImage} alt="Imagem de busca" title="Imagem usada na busca" />
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="no-results">
          <p>🔎 Pesquisando...</p>
        </div>
      ) : result.results.length === 0 && !error ? (
        <div className="no-results">
          <p>❌ Nenhuma pessoa encontrada</p>
        </div>
      ) : (
        <div className="results-list">
          {result.results.map((item, index) => (
            <div key={`${item.person_id}-${item.image_id}-${index}`} className="result-card">
              <div className="result-rank">#{index + 1}</div>

              {item.image_url && (
                <div className="result-image">
                  <img src={getImageUrl(item.image_url)} alt={item.key} />
                </div>
              )}

              <div className="result-info">
                <h4>{item.key}</h4>
                <p className="organization">
                  📦 {item.organization_name}
                </p>
                {item.image_id && (
                  <p className="image-id" title={item.image_id}>
                    🎫 ID: {item.image_id.substring(0, 8)}...
                  </p>
                )}
              </div>

              <div className="result-score">
                <div
                  className="score-bar"
                  style={{
                    width: `${item.similarity_score * 100}%`,
                    backgroundColor: getScoreColor(item.similarity_score)
                  }}
                />
                <p className="score-text">
                  {`${(item.similarity_score * 100).toFixed(2)}% de similaridade`}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FacialSearch;

